import { useEffect, useMemo, useState } from 'react';
import { Ban } from 'lucide-react';
import { useT } from '../../i18n';
import type { HomeProject } from '../../types';
import { RANK, lifecycleRank } from '../../utils/stages';
import { projectDisplayCode } from '../../utils/identifiers';
import { useStageName } from '../ProjectStageBar';
import { HEALTH_RANK, HealthLabel, projectHealth } from './health';
import { SponsoredProject } from './SponsoredProject';
import { prefetchHomeProject, useHomeProject } from './useHomeProject';

// "Mes projets sponsorisés": one dense strip of tabs, one per project, in this
// order — under way (Execution, Handover), planned (Planning), being prepared
// (Inbox → Qualification), and last the ones marked blocked, whatever their
// stage — each group with its own edge colour; the selected project is drawn
// whole below. Names are always shown in full. Parked and closed ones
// (Deferred, Backlog, Closed) stay out.

const SELECTED_KEY = 'pmo.home.sponsored';

const isParked = (label: string) => /^(deferred|backlog|closed|différé|reporté)$/i.test(label.trim());

type Group = 'running' | 'planning' | 'preparing' | 'blocked';
type Entry = { p: HomeProject; health: ReturnType<typeof projectHealth>; rank: number; group: Group };

const GROUPS: Group[] = ['running', 'planning', 'preparing', 'blocked'];
const GROUP_LABEL: Record<Group, string> = {
  running: 'home.sponsored.running', planning: 'home.sponsored.planning',
  preparing: 'home.sponsored.preparing', blocked: 'home.sponsored.blocked',
};

function groupOf(p: HomeProject, rank: number): Group {
  if (p.is_blocked) return 'blocked';
  if (rank === RANK.planning) return 'planning';
  return rank > RANK.planning ? 'running' : 'preparing';
}

export function SponsoredProjects({ projects, onTasksChanged }: { projects: HomeProject[]; onTasksChanged: () => void }) {
  const { t } = useT();
  const stageName = useStageName();

  const { all, counts } = useMemo(() => {
    const entries: Entry[] = [];
    for (const p of projects) {
      const label = p.status_label || '';
      if (isParked(label)) continue;
      const rank = lifecycleRank(label);
      if (rank > RANK.handover) continue;
      entries.push({ p, health: projectHealth(label, p.execution_health, p.milestones), rank, group: groupOf(p, rank) });
    }
    // By group; under way and planned: worst health first (red, orange, green),
    // Execution before Handover; the others furthest along first. Then by name.
    entries.sort((a, b) => GROUPS.indexOf(a.group) - GROUPS.indexOf(b.group)
      || (a.group === 'running' || a.group === 'planning'
        ? a.rank - b.rank || (a.health ? HEALTH_RANK[a.health.level] : 3) - (b.health ? HEALTH_RANK[b.health.level] : 3)
        : b.rank - a.rank)
      || a.p.name.localeCompare(b.p.name));
    const n = Object.fromEntries(GROUPS.map(g => [g, entries.filter(e => e.group === g).length])) as Record<Group, number>;
    return { all: entries, counts: n };
  }, [projects]);

  const [selected, setSelected] = useState<number | null>(() => {
    try { return Number(localStorage.getItem(SELECTED_KEY)) || null; } catch { return null; }
  });
  const currentId = all.find(x => x.p.id === selected)?.p.id ?? all[0]?.p.id ?? 0;
  useEffect(() => {
    if (!currentId) return;
    try { localStorage.setItem(SELECTED_KEY, String(currentId)); } catch { /* per-browser convenience only */ }
  }, [currentId]);

  // Once the page has settled, load the others one by one so switching is instant.
  const ids = all.map(x => x.p.id).join(',');
  useEffect(() => {
    let stop = false;
    const timer = setTimeout(async () => {
      for (const x of all) { if (stop) return; await prefetchHomeProject(x.p.id); }
    }, 1500);
    return () => { stop = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const { shownId, data, loading, error, reload } = useHomeProject(currentId);

  if (all.length === 0) return null;
  const watch = all.filter(x => x.group !== 'blocked' && x.health && x.health.level !== 'green').length;
  const shown = all.find(x => x.p.id === shownId) ?? all.find(x => x.p.id === currentId)!;

  const tab = ({ p, health, group }: Entry) => {
    const ms = p.milestones.filter(m => m.status !== 'cancelled');
    const done = ms.filter(m => m.status === 'completed').length;
    const active = p.id === currentId;
    return (
      <button key={p.id} type="button" role="tab" aria-selected={active}
        className={`home-ptab home-ptab--${group}${active ? ' is-active' : ''}`}
        onClick={() => setSelected(p.id)}
        onMouseEnter={() => prefetchHomeProject(p.id)} onFocus={() => prefetchHomeProject(p.id)}>
        <span className="home-ptab-top">
          <span className="home-code">{projectDisplayCode(p)}</span>
          {ms.length > 0 && <span className="home-mono">{t('home.sponsored.milestones', { done, total: ms.length })}</span>}
        </span>
        <span className="home-ptab-title">{p.name}</span>
        <span className="home-ptab-meta">
          {group === 'blocked' ? (
            <span className="home-ptab-blocked" title={p.blocked_reason || undefined}>
              <Ban size={11} aria-hidden="true" /> {t('projectDetail.blockedBadge')} · {stageName(p.status_label || '')}
            </span>
          ) : health ? <HealthLabel health={health} compact /> : <span>{stageName(p.status_label || '')}</span>}
        </span>
      </button>
    );
  };

  return (
    <section className="home-section" aria-labelledby="home-sponsored-title">
      <div className="home-section-head">
        <h2 className="section-label" id="home-sponsored-title">{t('home.sponsored.title')}</h2>
        {/* The counts double as the legend of the tabs' edge colours. */}
        <span className="home-section-summary home-plegend">
          {GROUPS.filter(g => counts[g] > 0).map(g => (
            <span key={g} className={`home-plegend-item home-plegend-item--${g}`}>
              <i aria-hidden="true" />{t(GROUP_LABEL[g])} <span className="home-count">{counts[g]}</span>
            </span>
          ))}
          {watch > 0 && <span className="home-late">{t('home.sponsored.watch', { n: watch })}</span>}
        </span>
      </div>

      <div className="home-ptabs" role="tablist" aria-label={t('home.sponsored.title')}>
        {all.map(tab)}
      </div>

      <div className="home-sponsor-detail" role="tabpanel">
        {error && <div className="pd-card home-message">{error}</div>}
        {!data && !error && <div className="pd-card home-message home-message--loading"><div className="spinner" /></div>}
        {data && <SponsoredProject project={shown.p} health={shown.health} data={data} loading={loading} onChanged={reload} onTasksChanged={onTasksChanged} />}
      </div>
    </section>
  );
}
